import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { BasicResourceConfigurationFormProps } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';

interface CurrencyUnit {
  id: string;
  name: string;
  value: number;
  icon: string;
  color: string;
  symbol: string;
}

export const CurrencyConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({ 
  resource, 
  config,
  onUpdate 
}) => {
  const { t } = useTranslation();
  const units = config?.units || resource.units || [
    { id: '1', name: 'Pièce de 1€', value: 1, icon: 'coins', color: 'bg-yellow-400', symbol: '1€' },
    { id: '2', name: 'Pièce de 2€', value: 2, icon: 'coins', color: 'bg-yellow-400', symbol: '2€' },
    { id: '5', name: 'Billet de 5€', value: 5, icon: 'dollar', color: 'bg-green-400', symbol: '5€' },
    { id: '10', name: 'Billet de 10€', value: 10, icon: 'dollar', color: 'bg-green-400', symbol: '10€' },
  ];

  const targetAmount = config?.targetAmount || resource.targetAmount || 15;
  const showTarget = config?.showTarget ?? resource.showTarget ?? true;
  const allowInteraction = config?.allowInteraction ?? resource.allowInteraction ?? true;
  const theme = config?.theme || resource.theme || 'primary';
  const prompt = config?.prompt || resource.prompt || '';
  const changeTarget = config?.changeTarget ?? resource.changeTarget ?? true;
  const maxTarget = config?.maxTarget ?? resource.maxTarget ?? 25;
  const minTarget = config?.minTarget ?? resource.minTarget ?? 5;

  const iconOptions = [
    { value: 'coins', label: t('currency.coins') },
    { value: 'dollar', label: t('currency.bills') },
    { value: 'euro', label: t('currency.euro') },
  ];

  const colorOptions = [
    { value: 'bg-yellow-400', label: t('currency.yellow') },
    { value: 'bg-green-400', label: t('currency.green') },
    { value: 'bg-blue-400', label: t('currency.blue') },
    { value: 'bg-red-400', label: t('currency.red') },
    { value: 'bg-purple-400', label: t('currency.purple') },
    { value: 'bg-gray-400', label: t('currency.gray') },
  ];

  const themeOptions = [
    { value: 'primary', label: t('currency.primary') },
    { value: 'secondary', label: t('currency.secondary') },
    { value: 'success', label: t('currency.success') },
    { value: 'warning', label: t('currency.warning') },
  ];

  const addUnit = () => {
    const newUnit: CurrencyUnit = {
      id: Date.now().toString(),
      name: t('currency.newUnit'),
      value: 1,
      icon: 'coins',
      color: 'bg-yellow-400',
      symbol: '1€',
    };
    const newUnits = [...units, newUnit];
    onUpdate({ units: newUnits });
  };

  const removeUnit = (id: string) => {
    const newUnits = units.filter((unit: CurrencyUnit) => unit.id !== id);
    onUpdate({ units: newUnits });
  };

  const updateUnit = (id: string, field: keyof CurrencyUnit, value: any) => {
    const newUnits = units.map((unit: CurrencyUnit) => 
      unit.id === id ? { ...unit, [field]: value } : unit
    );
    onUpdate({ units: newUnits });
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('currency.question')}
        </label>
        <textarea
          value={prompt}
          onChange={(e) => {
            onUpdate({ prompt: e.target.value });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder={t('currency.questionPlaceholder')}
        />
      </div>

      {/* Target Amount */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('currency.targetAmount')}
        </label>
        <input
          type="number"
          value={targetAmount}
          onChange={(e) => {
            const value = parseInt(e.target.value) || 0;
            onUpdate({ targetAmount: value });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          min="1"
          max="100"
        />
      </div>

      {/* Display Options */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-800">{t('currency.displayOptions')}</h4>
        
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={allowInteraction}
              onChange={(e) => {
                onUpdate({ allowInteraction: e.target.checked });
              }}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">{t('currency.allowInteraction')}</span>
          </label>
        </div>
        {allowInteraction && <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showTarget}
              onChange={(e) => {
                onUpdate({ showTarget: e.target.checked });
              }}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">{t('currency.showTarget')}</span>
          </label>
        </div>}
        {showTarget && <div className="flex items-center space-x-4">
            <label className="flex items-center">
                <input
                type="checkbox"
                checked={changeTarget}
                onChange={(e) => {
                    onUpdate({ changeTarget: e.target.checked });
                }}
                className="mr-2"
                />
                <span className="text-sm text-gray-700">{t('currency.changeTarget')}</span>
            </label>
        </div>}
        {changeTarget && <div className="flex space-x-6 w-fullgrid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('currency.minTarget')}
                </label>
                <input
                    type="number"
                    value={minTarget}
                    onChange={(e) => {
                        onUpdate({ minTarget: parseInt(e.target.value) || 0 });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('currency.maxTarget')}
                </label>
                <input
                    type="number"
                    value={maxTarget}
                    onChange={(e) => {
                        onUpdate({ maxTarget: parseInt(e.target.value) || 0 });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('currency.theme')}
          </label>
          <select
            value={theme}
            onChange={(e) => {
              onUpdate({ theme: e.target.value });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {themeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Currency Units */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-800">{t('currency.currencyUnits')}</h4>
          <button
            onClick={addUnit}
            className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('currency.addUnit')}</span>
          </button>
        </div>

        <div className="space-y-4">
          {units.map((unit: CurrencyUnit, index: number) => (
            <div key={unit.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-800">{t('currency.unit')} {index + 1}</h5>
                <button
                  onClick={() => removeUnit(unit.id)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('currency.name')}
                  </label>
                  <input
                    type="text"
                    value={unit.name}
                    onChange={(e) => updateUnit(unit.id, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('currency.value')}
                  </label>
                  <input
                    type="number"
                    value={unit.value}
                    onChange={(e) => updateUnit(unit.id, 'value', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('currency.symbol')}
                  </label>
                  <input
                    type="text"
                    value={unit.symbol}
                    onChange={(e) => updateUnit(unit.id, 'symbol', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1€"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('currency.icon')}
                  </label>
                  <select
                    value={unit.icon}
                    onChange={(e) => updateUnit(unit.id, 'icon', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {iconOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('currency.color')}
                  </label>
                  <select
                    value={unit.color}
                    onChange={(e) => updateUnit(unit.id, 'color', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {colorOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
